import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { PropertiesHeader } from '@/modules/common/components/PropertiesHeader';
import { PanelDisplayMode } from '@/modules/floorplanDesign/types';
import { useReadSelectionHeaderState } from '@/modules/floorplanDesign/hooks/';
import { FddmServiceFactory } from '@/modules/canvas/services/Floorplan/FddmServiceFactory';
import { useAppStore } from '@/modules/app/store/useAppStore';
import { AppServiceFactory } from '@/modules/app/service/AppServiceFactory';
import { useFloorplanDesignStore } from '@/modules/floorplanDesign/store/useFloorplanDesignStore';
import { isLassie, isLassieMini, isShuttleRack, TpsServiceFactory } from '@/modules/templating';
import { templatesToFlatCollection } from '@/modules/floorplanDesign/helpers/helpers';
import { assertNever } from '@/helpers/utils';
import { CanvasServiceFactory } from '@/modules/canvas/services/Canvas/CanvasServiceFactory';
import { logger } from '@/modules/common/logging/logger';
import { isHikStorage } from '@/modules/templating/guards';

interface Props {
  panelModeState: PanelDisplayMode;
}

export function SelectionHeader({ panelModeState }: Props) {
  const { t } = useTranslation();
  const tpsSvc = useMemo(() => TpsServiceFactory.getInstance().createTpsService(), []);
  const appSvc = useMemo(() => AppServiceFactory.getInstance().createAppService(), []);
  const canvasSvc = useMemo(() => CanvasServiceFactory.getInstance().createCanvasService(), []);
  const fddmSvc = useMemo(() => FddmServiceFactory.getInstance().createFddmService(), []);

  const { label, canDuplicateMulti, canTransformSelection } =
    useReadSelectionHeaderState(panelModeState);

  const onNameChange = useCallback(
    (value: string) => {
      if (!value) {
        return { error: t('errors:area.name.required') };
      }

      const id = useAppStore.getState().selectedIds[0];

      const template = useFloorplanDesignStore.getState().getTemplateById(id);
      if (template) {
        const { templates } = useFloorplanDesignStore.getState().design;
        const uniqueName = templatesToFlatCollection(templates)
          .filter((item) => item.group.id !== template.group.id)
          .every((item) => item.group.name !== value);
        if (!uniqueName) {
          return { error: t('errors:template.name.duplicated') };
        }

        if (isLassie(template)) {
          tpsSvc
            .regenerateLassieTemplate({
              ...template,
              group: {
                ...template.group,
                name: value,
              },
            })
            .then((result) => {
              if (result.result === 'success') {
                const updated = result.data.templates.lassies.at(0);

                if (!updated) {
                  logger.error('Expected an updated template in the tps regeneration response.');

                  return;
                }

                appSvc.setSelectedIds([updated.group.id]);
                canvasSvc.setSelection([updated.group.id]);
              }
            })
            .finally(() => {
              fddmSvc.deleteSelection([id]);
            });

          return;
        }

        if (isHikStorage(template)) {
          tpsSvc
            .regenerateHikTemplate({
              ...template,
              group: {
                ...template.group,
                name: value,
              },
            })
            .then((result) => {
              if (result.result === 'success') {
                const updated = result.data.templates?.hikStorages?.at(0);

                if (!updated) {
                  logger.error(
                    'Expected an updated template in the tps regeneration response.',
                  );

                  return;
                }

                appSvc.setSelectedIds([updated.group.id]);
                canvasSvc.setSelection([updated.group.id]);
              }
            })
            .finally(() => {
              fddmSvc.deleteSelection([id]);
            });

          return;
        }

        if (isLassieMini(template)) {
          tpsSvc
            .regenerateLassieMiniTemplate({
              ...template,
              group: {
                ...template.group,
                name: value,
              },
            })
            .then((result) => {
              if (result.result === 'success') {
                const updated = result.data.templates.lassieMinis.at(0);

                if (!updated) {
                  logger.error('Expected an updated template in the tps regeneration response.');

                  return;
                }

                appSvc.setSelectedIds([updated.group.id]);
                canvasSvc.setSelection([updated.group.id]);
              }
            })
            .finally(() => {
              fddmSvc.deleteSelection([id]);
            });

          return;
        }

        if (isShuttleRack(template)) {
          tpsSvc
            .regenerateShuttleTemplate({
              ...template,
              group: {
                ...template.group,
                name: value,
              },
            })
            .then((result) => {
              if (result.result === 'success') {
                const updated = result.data.templates.shuttleRacks.at(0);

                if (!updated) {
                  logger.error('Expected an updated template in the tps regeneration response.');

                  return;
                }

                appSvc.setSelectedIds([updated.group.id]);
                canvasSvc.setSelection([updated.group.id]);
              }
            })
            .finally(() => {
              fddmSvc.deleteSelection([id]);
            });

          return;
        }

        assertNever(template);
      }

      if (!FddmServiceFactory.getInstance().createFddmService().changeName(id, value)) {
        return { error: t('errors:area.name.duplicated') };
      }

      return undefined;
    },
    [appSvc, canvasSvc, fddmSvc, t, tpsSvc],
  );

  const onDuplicateMultiClick = useCallback(() => {
    AppServiceFactory.getInstance().createAppService().setActiveModalId('multi_copy_canvas');
  }, []);

  const deleteSelectedIds = useCallback(() => {
    const { selectedIds } = useAppStore.getState();

    if (selectedIds.length === 0) return;

    const appSvc = AppServiceFactory.getInstance().createAppService();
    appSvc.showLoadContainer('TRANSPARENT');

    try {
      // FddmServiceFactory.getInstance().createFddmService().deleteSelection(selectedIds);
      // appSvc.resetSelection();
      FddmServiceFactory.getInstance().createFddmService().deleteSelection(selectedIds);
      appSvc.resetSelection();
    } finally {
      appSvc.hideLoadContainer();
    }
  }, []);

  const onMirrorHorizontalClick = useCallback(() => {
    const { selectedIds } = useAppStore.getState();
    FddmServiceFactory.getInstance().createFddmService().mirrorSelectionHorizontally(selectedIds);
  }, []);

  const onMirrorVerticalClick = useCallback(() => {
    const { selectedIds } = useAppStore.getState();
    FddmServiceFactory.getInstance().createFddmService().mirrorSelectionVertically(selectedIds);
  }, []);

  const onRotate90Click = useCallback(() => {
    const { selectedIds } = useAppStore.getState();
    FddmServiceFactory.getInstance().createFddmService().rotateSelection90Degrees(selectedIds);
  }, []);

  return (
    <PropertiesHeader
      name={label}
      onNameChange={onNameChange}
      onDeleteClick={deleteSelectedIds}
      onDuplicateMultiClick={canDuplicateMulti ? onDuplicateMultiClick : undefined}
      onMirrorHorizontalClick={canTransformSelection ? onMirrorHorizontalClick : undefined}
      onMirrorVerticalClick={canTransformSelection ? onMirrorVerticalClick : undefined}
      onRotate90Click={canTransformSelection ? onRotate90Click : undefined}
      editable
    />
  );
}
